// External libraries
import { isFunction, isString } from '@checkout/react-components';

// Internal modules
import { conditionallyDropLoginCookie } from '../login';
import { $logger, LOG_IN_PAYLOAD } from '@/lib/logger';
import { isDev, logDevErrorToConsole } from '../env';
import { buildUrl, getRedirectUrl } from '../url';
import { appStore } from '@/store';
import { getAccessToken, getGlobals, getLocale, getToken } from '@/lib/globals';

const flushLogsAndRedirect = ({ logKey, logPayload, logMethod, url }) => {
  const logName = isString(logKey) ? logKey : 'redirect';
  const method = isFunction($logger[logMethod]) ? logMethod : 'info';
  $logger[method](logName, { ...logPayload, url });
  return $logger.flushThen(() => window.location.assign(url));
};

/**
 * @function redirect
 * @param {object} options
 */
const redirect = ({
  endpoint,
  logKey,
  logPayload,
  params = {},
  type = 'info',
} = {}) => {
  if (!isString(endpoint)) {
    $logger.error('weasley_attempted_redirect_with_invalid_url', {
      url: endpoint,
    });
    return redirectToGenericError();
  }

  if (isDev && type !== 'info') {
    return logDevErrorToConsole('PREVENT REDIRECT');
  }

  const { country, locale } = getLocale();
  const additionalParams = {
    'country.x': country,
    fromSignupLite: true,
    'locale.x': locale,
    token: getToken(),
    ...params,
  };
  const url = buildUrl(endpoint, { additionalParams });
  const logMethod = ['info', 'warn', 'error'].includes(type) ? type : 'info';

  flushLogsAndRedirect({ logKey, logMethod, logPayload, url });
};

/**
 * @function redirectToGenericError
 * @param {object} options | { message }
 * @describe Redirects to the generic error page
 */
const redirectToGenericError = ({ message = 'default' } = {}) => {
  const { uri } = getGlobals();
  const { base, genericErrorEndpoint } = uri;
  const params = { code: btoa(message) };

  if (isDev) {
    return logDevErrorToConsole(`GENERIC ERROR: ${message}`);
  }

  const { cancelUrl } = appStore.getState().api;
  if (cancelUrl) {
    params.cancelLink = cancelUrl;
  }

  redirect({
    endpoint: `${base}${genericErrorEndpoint}`,
    logKey: `weasley_redirect_to_generic_error_${message}`,
    params,
  });
};

/**
 * @function redirectToMerchantOnSuccess
 * @param {string} returnUrl The return url sent from the merchant
 */
const redirectToMerchantOnSuccess = returnUrl => {
  // If the returnUrl is not a string or has no length then we
  // don't have anywhere to send the user
  if (!isString(returnUrl) || !returnUrl) {
    $logger.warn('no_return_after_success');
    return;
  }

  $logger.info('return_to_merchant_after_success');

  flushLogsAndRedirect({ url: returnUrl });
};

/**
 * @function fallback
 * @param {object} options
 * @description triggers a fallback to xoon or hermes
 */
const fallback = (options = {}) => {
  const { additionalParams, app = 'xoon', payload = {}, reason } = options;

  if (isDev) {
    return logDevErrorToConsole(`FALLBACK: ${reason}`);
  }

  // If we have an access token we need to redirect to Hermes,
  // even if `app` is `'xoon'`, since the user cannot signup again
  const isSignUpToken = getAccessToken().type === 'signup';
  const appName = isSignUpToken ? 'hermes' : app;

  const redirectOptions = {
    endpoint: getRedirectUrl(appName),
    logKey: `weasley_fallback_to_${appName}`,
    params: {
      ...additionalParams,
      fallback: 1,
      reason: btoa(reason),
    },
    payload,
    type: 'error',
  };

  return conditionallyDropLoginCookie().then(() => {
    const { isUserLoggedIn } = appStore.getState().user;

    // If we are in a signup flow and the login failed, redirect
    // to the generic error page
    if (!isUserLoggedIn && isSignUpToken) {
      $logger.warn('redirect_to_generic_error_due_to_login_failure');
      return redirectToGenericError();
    }

    redirect(redirectOptions);
  });
};

const redirectToLogIn = () => {
  $logger.fpti('weasley_redirect_to_log_in', LOG_IN_PAYLOAD());
  redirect({
    endpoint: getRedirectUrl('hermes'),
    logKey: 'weasley_redirect_to_log_in',
    params: { hermesLoginRedirect: true },
  });
};

/**
 * @function redirectToXoon
 * @param {object} params
 * @description This function is used for non-fallback redirects to xoon.
 * An example of such a use case would be when the country is changed to a
 * non-lite supported country.
 */
const redirectToXoon = (params = {}, type) => {
  redirect({
    endpoint: getRedirectUrl('xoon'),
    logKey: 'weasley_redirect_to_xoon',
    params,
    type,
  });
};

/**
 * @function redirectToConsumer
 * @param {string} flowId
 * @param {object} params
 * @description This function is used for redirects to Consumer app.
 * An example of such a use case would be for the add bank case in JP
 */
const redirectToConsumer = (flowId, params = {}, type) => {
  const flowValue = flowId.replace(/^\?flow=/, '');

  redirect({
    endpoint: getRedirectUrl('tesla', { flow: flowValue }),
    logKey: 'weasley_redirect_to_tesla',
    params,
    type,
  });
};

export {
  fallback,
  flushLogsAndRedirect,
  redirect,
  redirectToConsumer,
  redirectToGenericError,
  redirectToLogIn,
  redirectToMerchantOnSuccess,
  redirectToXoon,
};
